import React, { useState, useEffect } from 'react';
import { 
    Table,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    Tooltip,
    TableContainer,
    Paper
} from '@mui/material';
import {
  makeStyles
}from "@mui/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Delete } from '@mui/icons-material';
import styled from 'styled-components';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';

import { AllMissionDataType } from '../types/MissionData';
import { StyledTable } from '../StyledComponents';
import { MissionType } from '../types/MissionType';
import AnswerDialog, { AnswerDialogDictionary } from './AnswerDialog';
import { deleteMissionRecord } from '../api';
import { firebaseUser } from '../firebase';
import { AppState } from '../reducers';
import { getColors } from '../resources/colors';
import { RoleType } from '../types/UserRole';
import SimpleSnackBar, { SnackBarProp } from './SimpleSnackBar';

import { useTranslation } from "react-i18next";

const playerColors = ['#FF6B6B', '#ffa726', '#66bb6a', '#29b6f6', '#345995', '#15616d', '#F6511D', '#7256a9', '#82ca9d', '#777b88', '#a18276', '#FFCAB1', '#88292F', '#ec407a'];

function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

const StyledGridHeader1 = styled.div`
    padding-top: 20px;
    padding-bottom: 10px;
    padding-left:10px;
    font-size: 20px;
    text-align: left;
`
interface DomainTaskProps {
    // data: Array<{ task_name: string, result: boolean | string, domain: Domain }>,
    data: Array<{ mission: string, score: number, totalScore: number }> | null,
    historyData: Array<AllMissionDataType>
    selectedTab: number,
    missionOnClick: (mission: Array<MissionType>) => void
}

const Row = (props: {
    data: { mission: string, score: number, totalScore: number, userHighestLevel: number, missionHighestLevel: number, accTime: number, lastPlayed: number | undefined },
    historyData: AllMissionDataType[],
    selectedMissions: Array<string>,
    setSelectedMissions: (missions: Array<string>) => void,
    missionOnClick: (mission: Array<MissionType>) => void,
    setAnswerDialogOpen: (x: boolean) => void,
    setAnswerHistory: (data: AllMissionDataType) => void,
    onSuccessDelete: (missionKey: string, missionPushKey: string) => void,
    color: string,
  }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [levelData, setLevelData] = useState<{ level: number, time: number, score: number }[]>();
    const [levelHistory, setLevelHistory] = useState<{ level: number, data: AllMissionDataType[] }[]>();
    const [color, setColor] = useState<string>();
    const loggedUserRole = useSelector<AppState, RoleType | null>(state => state.userRole);
    const loggedInUser = useSelector<AppState, firebaseUser | null>(state => state.loggedInUser);
  
    useEffect(() => {
      const levelArr = props.historyData.map(item => item.normalized_level).filter((value, index, self) => self.indexOf(value) === index);
      const temp1: { level: number, time: number, score: number }[] = [];
      const temp2: { level: number, data: AllMissionDataType[] }[] = [];
      levelArr.forEach(lv => {
        const tempData = props.historyData.filter(x => x.normalized_level === lv);
        temp2.push({
          level: lv,
          data: tempData,
        })
        setLevelHistory(temp2);
        temp1.push({
          level: lv,
          time: Math.round(_(tempData).meanBy(d => (d.task_end_time - d.task_start_time) / 1000) * 100) / 100,
          score: Math.round(_(tempData).meanBy(d => d.score) * 100) / 100
        })
      })
      setLevelData(temp1);
      const tempColor = hexToRgb(props.color);
      if (tempColor !== null) {
        const tempString = "rgba(" + tempColor.r + "," + tempColor.g + "," + tempColor.b + ",0.3)";
        setColor(tempString);
      }
    }, [props.data])
  
    const handleMissionOnClick = () => {
      setOpen(!open);
      if (open === false) {
        props.setSelectedMissions([...props.selectedMissions, props.data.mission]);
      } else {
        props.setSelectedMissions(props.selectedMissions.filter(x => x !== props.data.mission));
      }
    }
  
    const handleOpenAnswerDialog = (data: AllMissionDataType) => {
      props.setAnswerDialogOpen(true);
      props.setAnswerHistory(data);
    }

    const {t} = useTranslation()
  
    return (
      <React.Fragment>
        {color !== undefined &&
          <>
            <TableRow onClick={() => handleMissionOnClick()} hover>
              <TableCell>
                <IconButton size="small" onClick={() => handleMissionOnClick()}>
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </TableCell>
              <TableCell component="th" scope="row" style={{ textAlign: "center" }}>
                {t(`gameName.${props.data.mission}` as unknown as TemplateStringsArray)}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>{(props.data.score >= 0 ? (props.data.score) : "-")}/{props.data.totalScore}</TableCell>
              <TableCell style={{ textAlign: "center" }}>{props.data.userHighestLevel}/{props.data.missionHighestLevel}</TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {/* wording translation */}
                {/* https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/02-fromnow/ */}
                {moment.duration(props.data.accTime).humanize()
                .replace("a few ", t("domainTaskTable.a_few_"))
                .replace("a ", t("domainTaskTable.a_"))
                .replace("an ", t("domainTaskTable.an_"))
                .replace("seconds", t("domainTaskTable.seconds_"))
                .replace("minutes", t("domainTaskTable.minutes_"))
                .replace("minute", t("domainTaskTable.minute_"))
                .replace("hours", t("domainTaskTable.hours_"))
                .replace("hour", t("domainTaskTable.hour_"))
                .replace("days", t("domainTaskTable.days_"))
                .replace("day", t("domainTaskTable.day_"))
                .replace("months", t("domainTaskTable.months_"))
                .replace("month", t("domainTaskTable.month_"))
                .replace("years", t("domainTaskTable.years_"))
                .replace("year", t("domainTaskTable.year_"))}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {/* wording translation */}
                {/* https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/02-fromnow/ */}
                {moment(props.data.lastPlayed).fromNow()
                .replace("a few ", t("domainTaskTable.a_few_"))
                .replace("a ", t("domainTaskTable.a_"))
                .replace("an ", t("domainTaskTable.an_"))
                .replace("seconds ", t("domainTaskTable.seconds_"))
                .replace("minute ", t("domainTaskTable.minute_"))
                .replace("minutes ", t("domainTaskTable.minutes_"))
                .replace("hour ", t("domainTaskTable.hour_"))
                .replace("hours ", t("domainTaskTable.hours_"))
                .replace("day ", t("domainTaskTable.day_"))
                .replace("days ", t("domainTaskTable.days_"))
                .replace("month ", t("domainTaskTable.month_"))
                .replace("months ", t("domainTaskTable.months_"))
                .replace("year ", t("domainTaskTable.year_"))
                .replace("years ", t("domainTaskTable.years_"))
                .replace("ago", t("domainTaskTable.ago"))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <div style={{ width: "100%", overflow: "scroll" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: '#c1c1c1' }}>
                          <TableCell style={{ width: 100 }}> {t("domainTaskTable.level")}</TableCell>
                          <TableCell align="center" style={{ minWidth: 60 }}>{t("domainTaskTable.avgTimeForThisLv")}</TableCell>
                          <TableCell align="center" style={{ minWidth: 30 }}>{t("domainTaskTable.avgScoreForThisLv")}</TableCell>
                          <TableCell align="center" style={{ minWidth: 15 }}>{t("domainTaskTable.detailedAnswer")}</TableCell>
                        </TableRow>
                      </TableHead>
                      {levelData !== undefined && levelData.map((data, key) => (
                        <>
                          <TableRow key={key} style={{ backgroundColor: '#ececec' }}>
                            <TableCell>{t(`domainTaskTable.${data.level}` as unknown as TemplateStringsArray)}</TableCell>
                            <TableCell align="center">{data.time}{t("domainTaskTable.sec")}</TableCell>
                            <TableCell align="center">{(data.score >= 0 ? (data.score) : "-")}/10</TableCell>
                            <TableCell align="center">{t("domainTaskTable.action")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ width: 150 }}> {t("domainTaskTable.date")}</TableCell>
                            <TableCell align="center" style={{ minWidth: 60 }}>{t("domainTaskTable.reqTime")}</TableCell>
                            <TableCell align="center" style={{ minWidth: 60 }}>{t("domainTaskTable.score")}</TableCell>
                            <TableCell align="center" style={{ minWidth: 15 }}></TableCell>
                          </TableRow>
                          {levelHistory !== undefined && levelHistory.filter(x => x.level === data.level)[0].data.map((history, keys) => (
                            <MissionHistoryRecord user={loggedInUser} history={history} keys={keys} handleOpenAnswerDialog={handleOpenAnswerDialog} onSuccessDelete={props.onSuccessDelete} />
                          ))}
                        </>
                      ))}
                      {/* <TableHead>
                      <TableRow>
                        <TableCell style={{ width: 100 }}> 日期</TableCell>
                        <TableCell align="center">時間</TableCell>
                        <TableCell align="center" style={{ minWidth: 60 }}>所需時間</TableCell>
                        <TableCell align="center" style={{ minWidth: 30 }}>等級</TableCell>
                        <TableCell align="center" style={{ minWidth: 30 }}>分數</TableCell>
                        <TableCell align="center" style={{ minWidth: 60 }}>檢視答案</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.historyData.map((historyRow, key) => (
                        <TableRow key={key}>
                          <TableCell >{moment.utc(historyRow.task_start_time).local().format('YYYY年 MMM Do')}</TableCell>
                          <TableCell align="center">{moment.utc(historyRow.task_start_time).local().format('HH:mm:ss')}</TableCell>
                          <TableCell align="center">{((historyRow.task_end_time - historyRow.task_start_time) / 1000).toFixed(2)}s</TableCell>
                          <TableCell align="center">{historyRow.level}</TableCell>
                          {historyRow.score <= historyRow.total_score ?
                            <TableCell align="center">{historyRow.score} / {historyRow.total_score}</TableCell>
                            : <TableCell align="center">{null}</TableCell>}
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleOpenAnswerDialog(historyRow)}
                              color="inherit"
                            >
                              <VisibilityIcon color='primary' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody> */}
                    </Table>
                  </div>
                  {/* </Box> */}
                </Collapse>
              </TableCell>
            </TableRow>
          </>}
      </React.Fragment >
    );
}
  
const MissionHistoryRecord = (props: { user: firebaseUser | null, history: AllMissionDataType, keys: number, handleOpenAnswerDialog: (hist: AllMissionDataType) => void, onSuccessDelete: (missionKey: string, missionPushKey: string) => void }) => {
  
  const {t} = useTranslation()

  const [deleted, setDeleted] = useState<boolean>(false);
  const loggedUserRole = useSelector<AppState, RoleType | null>(state => state.userRole);

  const closeErrorSnackBar = () => {
    setSnackBar({ ...snackBar, "open": false });
  };

  const [snackBar, setSnackBar] = useState<SnackBarProp>({
    open: false,
    onClose: closeErrorSnackBar,
    message: "",
    variant: undefined
  });

  const tryDeleteMissionRecord = async (uid: string | undefined, missionKey: string, missionPushKey: string) => {
    const success = await deleteMissionRecord(uid, missionKey, missionPushKey);
    if (success) {
      props.onSuccessDelete(missionKey, missionPushKey);
      setDeleted(true);
      setSnackBar({ ...snackBar, open: true, variant: "success", message: t("domainTaskTable.deletedSuccessfully")});
    }
    else {
      setSnackBar({ ...snackBar, open: true, variant: "error", message: t("domainTaskTable.deletedUnsuccessfully")});
    }
  }

  return (
    <>
      <SimpleSnackBar open={snackBar.open} onClose={closeErrorSnackBar} message={snackBar.message} variant={snackBar.variant} autoClose={snackBar.autoClose} />
      <TableRow key={props.keys} style={{ display: deleted ? "none" : undefined }}>
        <Tooltip title={t("domainTaskTable.gameID") + props.history.push_key} arrow placement="top">
          <TableCell>{moment.utc(props.history.task_start_time).local().format('YYYY MMM Do')}</TableCell>
        </Tooltip>
        <TableCell align="center">{((props.history.task_end_time - props.history.task_start_time) / 1000).toFixed(2)}s</TableCell>
        <TableCell align="center">{(props.history.score >= 0 ? (props.history.score) : "-") + "/" + props.history.total_score}</TableCell>
        <TableCell align="center" style={{ display: "flex", justifyContent: "center" }}>
          <Tooltip arrow placement="top" title={AnswerDialogDictionary[props.history.key] ? t("domainTaskTable.clickForMore") : t("domainTaskTable.notAvaliable")}>
            <div>
              <IconButton
                color="primary"
                disabled={AnswerDialogDictionary[props.history.key] ? false : true}
                onClick={() => props.handleOpenAnswerDialog(props.history)}
              >
                <AssignmentIcon />
              </IconButton>
            </div>
          </Tooltip>
          {
            loggedUserRole === "admin" &&
            <Tooltip arrow placement="top" title={t("domainTaskTable.clickToDeleteRecord")}>
              <div>
                <IconButton
                  color="primary"
                  onClick={() => tryDeleteMissionRecord(props.user?.uid, props.history.key, props.history.push_key)}
                >
                  <Delete />
                </IconButton>
              </div>
            </Tooltip>
          }
        </TableCell>
      </TableRow>
    </>
  )
}
  
export default function DomainTaskTable(props: DomainTaskProps) {

  const {t} = useTranslation()

  const [selectedMissions, setSelectedMissions] = useState<Array<MissionType>>([]);
  const [answerDialogOpen, setAnswerDialogOpen] = useState<boolean>(false);
  const [answerHistory, setAnswerHistory] = useState<AllMissionDataType>();
  const [missionHistory, setMissionHistory] = useState<AllMissionDataType[]>();
  const classes = useStyles();

  const handleCloseAnswerDialog = () => {
    setAnswerDialogOpen(false);
  }

  useEffect(() => {
    setMissionHistory(props.historyData);
  }, [props.historyData]);

  const deleteMissionEntry = (missionKey: string, missionPushKey: string) => {
    if (!missionHistory) return;

    setMissionHistory(prev => prev?.filter(hist => hist.push_key !== missionPushKey));
  }
  const mapTableRow = () => {
    const colors = getColors(props.data!.length);

    return props.data!.map((row, index) => (
      (row !== undefined) &&
      <Row
        key={row.mission}
        data={row as any}
        historyData={missionHistory?.filter(x => x.key === row.mission) ?? []}
        missionOnClick={props.missionOnClick}
        selectedMissions={selectedMissions}
        setSelectedMissions={setSelectedMissions}
        setAnswerDialogOpen={setAnswerDialogOpen}
        setAnswerHistory={setAnswerHistory}
        color={colors[index]}
        onSuccessDelete={deleteMissionEntry}
      />
    ));
  }

  useEffect(() => {
    props.missionOnClick(selectedMissions);
  }, [selectedMissions])

  return (
    <>
      {(props.data !== null) ?
        <TableContainer component={Paper}>
          <Table>
            <TableRow style={{ backgroundColor: '#f2f2f2' }}>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.expandDetails")}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.missionName")}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.avgScore")}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.maxLv")}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.accumulatedGameTime")}
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                {t("domainTaskTable.lastGameTime")}
              </TableCell>
            </TableRow>
            {mapTableRow()}
            {answerDialogOpen && answerHistory !== undefined &&
              <AnswerDialog data={answerHistory} open={answerDialogOpen} onClose={handleCloseAnswerDialog} />}
          </Table>
        </TableContainer>
        :
        props.selectedTab > 0 ?
          <div style={{ marginTop: 20, marginBottom: 20 }}>{t("domainTaskTable.errMsg")}</div>
          :
          <div style={{ marginTop: 20, marginBottom: 20 }}>{t("domainTaskTable.plzSelect")}</div>
      }
    </>
  )
}